import * as THREE from "three";
import { el } from "./dom.js";
import { api } from "../net/api.js";
import { parseNBT, decodeAnySchematic, mcNameColor, mcSolid, mcStructure, mcVegetation, mcNaturalWood, classifyBlock, type BlockAction, paletteName, blockStateIndex, makePalette, MAX_GRID_CELLS, mergeGrid, detectScenes, downsampleGrid, type ScanChunk, type Scene } from "./mcImport.js";

/**
 * Community MAP EDITOR (task #3) — a 3D, Minecraft-like block editor. You orbit
 * around your map (left-drag) and click to PLACE a block (on the ground or on a
 * face of an existing block) or REMOVE one, plus drop spawn points. Save /
 * Publish to the backend; play it from Play → 🎨 Custom. Login-gated.
 *
 * Blocks snap to a grid of the current brush size; each block exports to one
 * MapDef box. A floor slab + spawns are auto-added on export so maps are
 * grounded. Large Minecraft imports are allowed; play rendering merges them
 * into spatial cells so the whole map is not drawn as separate meshes at once.
 */

// ---- Minecraft import (.schematic + Anvil .mca region files) ---------------
/** Gunzip an ArrayBuffer using the browser's native DecompressionStream. */
async function decompress(buf: ArrayBuffer, format: "gzip" | "deflate"): Promise<ArrayBuffer> {
  const ds = new (globalThis as unknown as { DecompressionStream: new (f: string) => GenericTransformStream }).DecompressionStream(format);
  return await new Response(new Blob([buf]).stream().pipeThrough(ds)).arrayBuffer();
}
function regionCoords(file: File): { rx: number; rz: number } | null {
  const m = /(?:^|\/)r\.(-?\d+)\.(-?\d+)\.mca$/i.exec((file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name);
  return m ? { rx: Number(m[1]), rz: Number(m[2]) } : null;
}
function isOverworldRegionFile(file: File): boolean {
  if (!/\.mca$/i.test(file.name)) return false;
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  if (!rel) return true;
  const parts = rel.split("/").filter(Boolean);
  if (parts.some((p) => /^DIM-?\d+$/i.test(p))) return false;
  const regionIndex = parts.findIndex((p) => p.toLowerCase() === "region");
  return regionIndex >= 0 && parts.length === regionIndex + 2 && /^r\.-?\d+\.-?\d+\.mca$/i.test(parts[regionIndex + 1]);
}

interface Block { x: number; y: number; z: number; sx: number; sy: number; sz: number; color: string; mesh: THREE.Mesh; tag?: string; }
interface Spawn { x: number; z: number; yaw: number; mesh: THREE.Group; }
type AutoPickupKind = "health" | "armor" | "grenade" | "speed" | "dmg" | "cloak" | "imperion" | "antigrav" | "decoy" | "jetpack" | "rocket" | "fartblaster" | "beigechest" | "waterchest" | "music";
type AutoVehicleKind = "kart" | "heli" | "speeder" | "artillery" | "gunship" | "interceptor";
type SurfaceSpawn = { x: number; y: number; z: number; yaw: number };

const MAX_BLOCKS = 30000;

export function editorScreen(opts: { onBack: () => void }): HTMLElement {
  const screen = el("div", { class: "screen editor-screen" });

  if (api.guest || !api.account) {
    const back = el("button", { class: "primary wide mt1", text: "‹ Back" });
    back.onclick = opts.onBack;
    screen.append(el("div", { class: "panel", style: "max-width:420px;text-align:center" },
      el("h2", { text: "Map Builder" }),
      el("p", { class: "hint", text: "Create a free account to build and publish your own maps." }), back));
    return screen;
  }

  // ---- three.js scene ----
  const canvas = document.createElement("canvas");
  canvas.className = "editor-canvas";
  const cropRectDiv = el("div", { style: "position:absolute;display:none;border:1.5px dashed #7fd0ff;background:rgba(90,170,255,0.14);pointer-events:none;z-index:4" });
  const canvasWrap = el("div", { class: "editor-canvas-wrap", style: "position:relative" }, canvas, cropRectDiv);
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
  const scene = new THREE.Scene();
  scene.background = new THREE.Color("#0a0d16");
  const camera = new THREE.PerspectiveCamera(60, 1, 0.1, 2000);
  scene.add(new THREE.AmbientLight(0xffffff, 0.7));
  const sun = new THREE.DirectionalLight(0xffffff, 0.9); sun.position.set(40, 80, 30); scene.add(sun);
  const grid = new THREE.GridHelper(200, 100, 0x2a3550, 0x141c2e); scene.add(grid);
  // invisible ground plane for raycasting placement on the floor
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(400, 400), new THREE.MeshBasicMaterial({ visible: false }));
  ground.rotation.x = -Math.PI / 2; scene.add(ground);

  // orbit camera state
  let az = Math.PI * 0.25, pol = Math.PI * 0.32, rad = 90;
  const target = new THREE.Vector3(0, 4, 0);
  const applyCam = (): void => {
    camera.position.set(
      target.x + rad * Math.sin(pol) * Math.sin(az),
      target.y + rad * Math.cos(pol),
      target.z + rad * Math.sin(pol) * Math.cos(az));
    camera.lookAt(target);
  };

  // ---- editor state ----
  const blocks: Block[] = [];
  const spawns: Spawn[] = [];
  let tool: "place" | "remove" | "paint" | "spawn" = "place";
  let color = "#c07680";
  let brushX = 4, brushY = 4, brushZ = 4;
  let theme: "neon" | "day" = "day";
  let currentId: string | null = null;
  let name = "My Map";
  let importCrop = 512;   // auto-tightened/expanded from the detected build footprint
  let importYMin = 58;    // surface-ish up: skip deep underground (keeps box count sane)
  let importYMax = 165;   // capture tall towers
  let importStep = 1;
  let importScale = 1;
  // "surface" keeps ALL solid exposed blocks (terrain, foliage, buildings) — the
  // recognisable result. "structure" strips to structure blocks only, which turns
  // trees into bare trunk-poles and floats buildings with no ground (nonsense),
  // so it is NOT the default. The build auto-centering runs in every mode below.
  let importDetail: "structure" | "surface" | "solid" = "surface";
  let importCenterX = 0;
  let importCenterZ = 0;
  let importCenterXInput: HTMLInputElement | null = null;
  let importCenterZInput: HTMLInputElement | null = null;

  const geoCache = new Map<string, THREE.BoxGeometry>();
  const boxGeo = (sx: number, sy: number, sz: number): THREE.BoxGeometry => {
    const key = `${sx}:${sy}:${sz}`;
    let g = geoCache.get(key);
    if (!g) { g = new THREE.BoxGeometry(sx, sy, sz); geoCache.set(key, g); }
    return g;
  };
  // Materials and edge geometries are SHARED per colour/size — a big import
  // otherwise allocates tens of thousands of materials and EdgesGeometries.
  const matCache = new Map<string, THREE.MeshLambertMaterial>();
  const blockMat = (c: string): THREE.MeshLambertMaterial => {
    let m = matCache.get(c);
    if (!m) { m = new THREE.MeshLambertMaterial({ color: c }); matCache.set(c, m); }
    return m;
  };
  const edgeCache = new Map<string, THREE.EdgesGeometry>();
  const edgeMat = new THREE.LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.25 });
  const EDGE_LIMIT = 12000; // past this, skip outline meshes to keep the editor responsive
  const status = el("div", { class: "editor-status hint" });
  const setStatus = (m: string, err = false): void => { status.textContent = m; status.style.color = err ? "#ff6b6b" : ""; };
  // import progress bar
  const progFill = el("div", { class: "editor-prog-fill" });
  const progWrap = el("div", { class: "editor-prog", style: "display:none" }, progFill);
  let progressHideTimer: ReturnType<typeof window.setTimeout> | null = null;
  const setProgress = (frac: number | null, label?: string): void => {
    if (frac === null) { progWrap.style.display = "none"; return; }
    if (progressHideTimer) { window.clearTimeout(progressHideTimer); progressHideTimer = null; }
    progWrap.style.display = "block";
    progFill.style.width = `${Math.max(0, Math.min(1, frac)) * 100}%`;
    if (label !== undefined) setStatus(label);
  };
  const notice = el("div", { class: "editor-notice", style: "display:none" });
  let noticeTimer: ReturnType<typeof window.setTimeout> | null = null;
  const notify = (m: string): void => {
    notice.textContent = m;
    notice.className = "editor-notice";
    notice.style.display = "block";
    if (noticeTimer) window.clearTimeout(noticeTimer);
    noticeTimer = window.setTimeout(() => { notice.style.display = "none"; }, 3600);
  };
  const finishProgress = (m: string): void => {
    if (progressHideTimer) window.clearTimeout(progressHideTimer);
    setProgress(1, m);
    notify(m);
    progressHideTimer = window.setTimeout(() => setProgress(null), 700);
  };
  const tick = (): Promise<void> => new Promise((r) => setTimeout(r, 0)); // yield so the bar repaints
  const refreshCount = (): void => setStatus(`${blocks.length}/${MAX_BLOCKS} blocks · ${spawns.length} spawns${blocks.length >= MAX_BLOCKS ? " · limit reached" : ""}`);

  const spawnMarker = (): THREE.Group => {
    const g = new THREE.Group();
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 4, 8), new THREE.MeshBasicMaterial({ color: "#37d67a" }));
    post.position.y = 2;
    const head = new THREE.Mesh(new THREE.ConeGeometry(1.2, 2, 8), new THREE.MeshBasicMaterial({ color: "#37d67a" }));
    head.position.y = 5;
    g.add(post, head);
    return g;
  };
  const addSpawn = (x: number, z: number, yaw = 0): void => {
    const g = spawnMarker();
    g.position.set(x, 0, z);
    scene.add(g);
    spawns.push({ x, z, yaw, mesh: g });
  };

  const addBlock =(x: number, y: number, z: number, sx: number, sy: number, sz: number, c: string, tag?: string): void => {
    if (blocks.length >= MAX_BLOCKS) { refreshCount(); return; }
    const geom = boxGeo(sx, sy, sz);
    const mesh = new THREE.Mesh(geom, blockMat(c));
    mesh.position.set(x, y, z);
    if (blocks.length < EDGE_LIMIT) {
      const key = `${sx}:${sy}:${sz}`;
      let eg = edgeCache.get(key);
      if (!eg) { eg = new THREE.EdgesGeometry(geom); edgeCache.set(key, eg); }
      mesh.add(new THREE.LineSegments(eg, edgeMat));
    }
    scene.add(mesh);
    blocks.push({ x, y, z, sx, sy, sz, color: c, mesh, tag });
    refreshCount();
  };

  const removeBlockMesh = (mesh: THREE.Object3D): void => {
    const i = blocks.findIndex((b) => b.mesh === mesh || b.mesh === mesh.parent);
    if (i < 0) return;
    scene.remove(blocks[i].mesh); // material + edges are shared caches — do not dispose
    blocks.splice(i, 1); refreshCount();
  };
  const paintBlockMesh = (mesh: THREE.Object3D): void => {
    const b = blocks.find((bb) => bb.mesh === mesh || bb.mesh === mesh.parent);
    if (!b) return;
    b.color = color;
    b.mesh.material = blockMat(color); // swap the shared material, never mutate it
  };

  const snap = (v: number, s: number): number => (Math.floor(v / s) + 0.5) * s;
  const snapCenter = (v: number, size: number): number => snap(v, Math.max(1, size));

  // ---- interaction: left-drag orbits, wheel zooms, click acts per tool ----
  const ray = new THREE.Raycaster();
  const ndc = new THREE.Vector2();

  // ---- top-view crop: drag a rectangle from above, trim everything outside ----
  let cropMode = false;
  let cropSaved: { az: number; pol: number; rad: number; tx: number; ty: number; tz: number } | null = null;
  let cropDrag: { x0: number; y0: number; x1: number; y1: number } | null = null;
  let cropWorld: { minX: number; maxX: number; minZ: number; maxZ: number } | null = null;
  const cropPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const cropHitA = new THREE.Vector3();
  const cropHitB = new THREE.Vector3();
  const updateCropRectDiv = (): void => {
    if (!cropDrag) { cropRectDiv.style.display = "none"; return; }
    cropRectDiv.style.display = "block";
    cropRectDiv.style.left = `${Math.min(cropDrag.x0, cropDrag.x1)}px`;
    cropRectDiv.style.top = `${Math.min(cropDrag.y0, cropDrag.y1)}px`;
    cropRectDiv.style.width = `${Math.abs(cropDrag.x1 - cropDrag.x0)}px`;
    cropRectDiv.style.height = `${Math.abs(cropDrag.y1 - cropDrag.y0)}px`;
  };
  const groundPoint = (px: number, py: number, out: THREE.Vector3): boolean => {
    const r = canvas.getBoundingClientRect();
    ndc.set((px / r.width) * 2 - 1, -(py / r.height) * 2 + 1);
    ray.setFromCamera(ndc, camera);
    // math plane, not the 400×400 ground mesh — imports can be far wider
    return ray.ray.intersectPlane(cropPlane, out) !== null;
  };

  let down: { x: number; y: number } | null = null, moved = false;
  canvas.addEventListener("mousedown", (e) => {
    if (cropMode) {
      const r = canvas.getBoundingClientRect();
      cropDrag = { x0: e.clientX - r.left, y0: e.clientY - r.top, x1: e.clientX - r.left, y1: e.clientY - r.top };
      cropWorld = null;
      updateCropRectDiv();
      return;
    }
    down = { x: e.clientX, y: e.clientY }; moved = false;
  });
  canvas.addEventListener("mousemove", (e) => {
    if (cropMode) {
      if (!cropDrag) return;
      const r = canvas.getBoundingClientRect();
      cropDrag.x1 = e.clientX - r.left; cropDrag.y1 = e.clientY - r.top;
      updateCropRectDiv();
      return;
    }
    if (!down) return;
    const dx = e.clientX - down.x, dy = e.clientY - down.y;
    if (Math.abs(dx) + Math.abs(dy) > 4) moved = true;
    az -= dx * 0.008; pol = Math.max(0.15, Math.min(Math.PI / 2 - 0.02, pol - dy * 0.006));
    down = { x: e.clientX, y: e.clientY };
    if (moved) applyCam();
  });
  canvas.addEventListener("mouseup", (e) => {
    if (cropMode) {
      if (!cropDrag) return;
      const drag = cropDrag;
      cropDrag = null;
      if (Math.abs(drag.x1 - drag.x0) < 6 || Math.abs(drag.y1 - drag.y0) < 6) { cropRectDiv.style.display = "none"; return; }
      if (groundPoint(drag.x0, drag.y0, cropHitA) && groundPoint(drag.x1, drag.y1, cropHitB)) {
        cropWorld = {
          minX: Math.min(cropHitA.x, cropHitB.x), maxX: Math.max(cropHitA.x, cropHitB.x),
          minZ: Math.min(cropHitA.z, cropHitB.z), maxZ: Math.max(cropHitA.z, cropHitB.z),
        };
        setStatus(`Selected ${Math.round(cropWorld.maxX - cropWorld.minX)}×${Math.round(cropWorld.maxZ - cropWorld.minZ)} — Apply crop to trim, or drag again.`);
      }
      return; // keep the drawn rectangle visible until Apply/Cancel/redraw
    }
    const wasClick = down && !moved; down = null;
    if (!wasClick) return;
    const r = canvas.getBoundingClientRect();
    ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    ray.setFromCamera(ndc, camera);
    if (tool === "remove") {
      const hit = ray.intersectObjects(blocks.map((b) => b.mesh), true)[0];
      if (hit) { pushHistory(); removeBlockMesh(hit.object); return; }
      const spawnHit = ray.intersectObjects(spawns.map((s) => s.mesh), true)[0];
      if (spawnHit) {
        const i = spawns.findIndex((s) => s.mesh === spawnHit.object || s.mesh === spawnHit.object.parent);
        if (i >= 0) { pushHistory(); scene.remove(spawns[i].mesh); spawns.splice(i, 1); refreshCount(); }
      }
      return;
    }
    if (tool === "paint") {
      const hit = ray.intersectObjects(blocks.map((b) => b.mesh), true)[0];
      if (hit) {
        const b = blocks.find((bb) => bb.mesh === hit.object || bb.mesh === hit.object.parent);
        if (b && b.color !== color) { pushHistory(); paintBlockMesh(hit.object); }
      }
      return;
    }
    // place / spawn: hit a block face first, else the ground
    const blockHit = ray.intersectObjects(blocks.map((b) => b.mesh), true)[0];
    const groundHit = ray.intersectObject(ground)[0];
    if (tool === "spawn") {
      const p = blockHit?.point ?? groundHit?.point; if (!p) return;
      pushHistory();
      addSpawn(snap(p.x, 2), snap(p.z, 2), 0); refreshCount();
      return;
    }
    // PLACE
    if (blockHit && blockHit.face) {
      if (blocks.length >= MAX_BLOCKS) { refreshCount(); return; }
      const b = blocks.find((bb) => bb.mesh === blockHit.object || bb.mesh === blockHit.object.parent)!;
      const n = blockHit.face.normal;
      pushHistory();
      addBlock(
        b.x + n.x * (b.sx / 2 + brushX / 2),
        b.y + n.y * (b.sy / 2 + brushY / 2),
        b.z + n.z * (b.sz / 2 + brushZ / 2),
        brushX, brushY, brushZ, color,
      );
    } else if (groundHit) {
      if (blocks.length >= MAX_BLOCKS) { refreshCount(); return; }
      pushHistory();
      addBlock(snapCenter(groundHit.point.x, brushX), brushY / 2, snapCenter(groundHit.point.z, brushZ), brushX, brushY, brushZ, color);
    }
  });
  canvas.addEventListener("mouseleave", () => { down = null; cropDrag = null; });
  canvas.addEventListener("wheel", (e) => { e.preventDefault(); rad = Math.max(15, Math.min(300, rad + Math.sign(e.deltaY) * 6)); applyCam(); }, { passive: false });
  canvas.addEventListener("contextmenu", (e) => e.preventDefault());

  // ---- render loop ----
  let running = true;
  const resize = (): void => {
    const w = canvas.clientWidth || 680, h = canvas.clientHeight || 680;
    renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
  };
  const loop = (): void => { if (!running) return; requestAnimationFrame(loop); renderer.render(scene, camera); };
  window.addEventListener("resize", resize);

  // ---- export / import ----
  const mapFootprint = (): { minX: number; maxX: number; minZ: number; maxZ: number; width: number; depth: number; span: number } => {
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const b of blocks) {
      minX = Math.min(minX, b.x - b.sx / 2); maxX = Math.max(maxX, b.x + b.sx / 2);
      minZ = Math.min(minZ, b.z - b.sz / 2); maxZ = Math.max(maxZ, b.z + b.sz / 2);
    }
    if (!Number.isFinite(minX)) { minX = -30; maxX = 30; minZ = -30; maxZ = 30; }
    const width = Math.max(1, maxX - minX), depth = Math.max(1, maxZ - minZ);
    return { minX, maxX, minZ, maxZ, width, depth, span: Math.max(width, depth) };
  };

  const autoGameplay = (): {
    pickups: { p: [number, number, number]; kind: AutoPickupKind }[];
    vehicles: { p: [number, number, number]; yaw: number; kind: AutoVehicleKind }[];
    jumpPads: { p: [number, number, number]; power?: number; color?: string; fling?: number }[];
  } => {
    const fp = mapFootprint();
    const cx = (fp.minX + fp.maxX) / 2, cz = (fp.minZ + fp.maxZ) / 2;
    const topBlocks = blocks
      .filter((b) => b.sx >= 1.8 && b.sz >= 1.8 && b.y + b.sy / 2 >= -0.1)
      .sort((a, b) => Math.hypot(a.x - cx, a.z - cz) - Math.hypot(b.x - cx, b.z - cz));
    const used: [number, number][] = spawns.map((s) => [s.x, s.z]);
    const clearAbove = (x: number, y: number, z: number, half: number, tall: number): boolean => {
      for (const b of blocks) {
        if (Math.abs(x - b.x) >= b.sx / 2 + half || Math.abs(z - b.z) >= b.sz / 2 + half) continue;
        const bottom = b.y - b.sy / 2, top = b.y + b.sy / 2;
        if (y < top && y + tall > bottom) return false;
      }
      return true;
    };
    const take = (n: number, half: number, tall: number, spacing: number, minSpawn: number, high = false): [number, number, number][] => {
      const out: [number, number, number][] = [];
      const ordered = high
        ? [...topBlocks].sort((a, b) => (b.y + b.sy / 2) - (a.y + a.sy / 2) || Math.hypot(a.x - cx, a.z - cz) - Math.hypot(b.x - cx, b.z - cz))
        : topBlocks;
      for (const b of ordered) {
        if (out.length >= n) break;
        const x = b.x, z = b.z, y = b.y + b.sy / 2 + 0.2;
        if (x < fp.minX + 3 || x > fp.maxX - 3 || z < fp.minZ + 3 || z > fp.maxZ - 3) continue;
        if (!clearAbove(x, y, z, half, tall)) continue;
        if (spawns.some((s) => Math.hypot(s.x - x, s.z - z) < minSpawn)) continue;
        if (used.some(([ux, uz]) => Math.hypot(ux - x, uz - z) < spacing)) continue;
        out.push([x, y, z]); used.push([x, z]);
      }
      return out;
    };

    const scale = Math.max(0, Math.min(1, (fp.span - 55) / 170));
    const pickupCount = Math.round(7 + scale * 9);
    const padCount = Math.round(3 + scale * 5);
    const vehicleCount = fp.span < 70 ? 0 : Math.round(2 + scale * 3);
    const pickupKinds: AutoPickupKind[] = [
      "health", "armor", "grenade", "speed", "dmg", "cloak", "health", "armor",
      "imperion", "jetpack", "rocket", "decoy", "fartblaster", "beigechest", "waterchest", "antigrav", "music",
    ];
    const pickups = take(pickupCount, 1.1, 2.2, Math.max(7, fp.span / 13), 5).map((p, i) => ({
      p: [p[0], p[1] + 0.1, p[2]] as [number, number, number],
      kind: pickupKinds[i % pickupKinds.length],
    }));
    const jumpPads = take(padCount, 1.4, 4.5, Math.max(12, fp.span / 9), 6, true).map((p, i) => ({
      p: [p[0], p[1] - 0.05, p[2]] as [number, number, number],
      power: i % 3 === 0 ? 48 : 28,
      color: i % 3 === 0 ? "#a54dff" : "#ff8a2a",
      fling: i % 3 === 0 ? 0 : undefined,
    }));
    const vehicleKinds: AutoVehicleKind[] = ["kart", "speeder", "heli", "artillery", "interceptor", "gunship"];
    const vehicles = take(vehicleCount, 2.2, 2.4, Math.max(18, fp.span / 6), 8).map((p, i) => {
      const kind = vehicleKinds[i % vehicleKinds.length];
      return {
        p: [p[0], p[1] + (kind === "heli" ? 0.7 : 0.35), p[2]] as [number, number, number],
        yaw: (i % 4) * Math.PI * 0.5,
        kind,
      };
    });
    return { pickups, vehicles, jumpPads };
  };

  const autoSpawns = (): SurfaceSpawn[] => {
    const fp = mapFootprint();
    const cx = (fp.minX + fp.maxX) / 2, cz = (fp.minZ + fp.maxZ) / 2;
    const topBlocks = blocks
      .filter((b) => b.sx >= 2.2 && b.sz >= 2.2 && b.y + b.sy / 2 >= -0.1)
      .sort((a, b) => Math.hypot(b.x - cx, b.z - cz) - Math.hypot(a.x - cx, a.z - cz));
    const clearAt = (x: number, y: number, z: number): boolean => {
      for (const b of blocks) {
        if (Math.abs(x - b.x) >= b.sx / 2 + 0.65 || Math.abs(z - b.z) >= b.sz / 2 + 0.65) continue;
        const bottom = b.y - b.sy / 2, top = b.y + b.sy / 2;
        if (y < top && y + 2.1 > bottom) return false;
      }
      return true;
    };
    const escapeScore = (x: number, y: number, z: number): number => {
      let score = 0;
      for (let i = 0; i < 8; i++) {
        const a = i * Math.PI / 4;
        const dx = Math.sin(a), dz = Math.cos(a);
        for (const dist of [3, 6, 10]) {
          if (clearAt(x + dx * dist, y, z + dz * dist)) score++;
          else break;
        }
      }
      return score;
    };
    const spawnClear = (x: number, y: number, z: number): boolean => clearAt(x, y, z) && escapeScore(x, y, z) >= 10;
    const surfaceAt = (x: number, z: number): number | null => {
      let best: number | null = null;
      for (const b of topBlocks) {
        if (Math.abs(x - b.x) > b.sx / 2 - 0.5 || Math.abs(z - b.z) > b.sz / 2 - 0.5) continue;
        const y = b.y + b.sy / 2 + 0.15;
        if ((best === null || y > best) && spawnClear(x, y, z)) best = y;
      }
      return best;
    };
    if (spawns.length > 1) {
      return spawns.map((s): SurfaceSpawn => {
        const y = surfaceAt(s.x, s.z);
        if (y !== null) return { ...s, y };
        const nearest = topBlocks.find((b) => spawnClear(b.x, b.y + b.sy / 2 + 0.15, b.z));
        return nearest ? { x: nearest.x, z: nearest.z, yaw: s.yaw, y: nearest.y + nearest.sy / 2 + 0.15 } : { ...s, y: 0.15 };
      });
    }
    const targetN = Math.round(8 + Math.max(0, Math.min(1, (fp.span - 70) / 170)) * 8);
    const out: SurfaceSpawn[] = [];
    for (const b of topBlocks) {
      if (out.length >= targetN) break;
      const y = b.y + b.sy / 2 + 0.15;
      if (b.x < fp.minX + 4 || b.x > fp.maxX - 4 || b.z < fp.minZ + 4 || b.z > fp.maxZ - 4) continue;
      if (!spawnClear(b.x, y, b.z)) continue;
      const minSep = Math.max(12, fp.span / 8);
      if (out.some((s) => Math.hypot(s.x - b.x, s.z - b.z) < minSep)) continue;
      out.push({ x: b.x, z: b.z, y, yaw: Math.atan2(cx - b.x, cz - b.z) });
    }
    return out.length ? out : [{ x: 0, z: 0, y: 0.15, yaw: 0 }];
  };

  const toMapData = (): unknown => {
    // bounding box of everything → an auto floor slab (top y=0) so it's grounded
    let mnx = -30, mxx = 30, mnz = -30, mxz = 30;
    for (const b of [...blocks.map((b) => ({ x: b.x, z: b.z, sx: b.sx, sz: b.sz })), ...spawns.map((s) => ({ x: s.x, z: s.z, sx: 4, sz: 4 }))]) {
      mnx = Math.min(mnx, b.x - b.sx / 2); mxx = Math.max(mxx, b.x + b.sx / 2);
      mnz = Math.min(mnz, b.z - b.sz / 2); mxz = Math.max(mxz, b.z + b.sz / 2);
    }
    const floor = { p: [(mnx + mxx) / 2, -0.5, (mnz + mxz) / 2], s: [mxx - mnx + 8, 1, mxz - mnz + 8], color: "#3a4152" };
    // The API caps every box SIZE component at 2000 (validateUserMap →
    // isVec3(s, 2000); the server mirrors it). Two producers here scale with the
    // map and can exceed it: the auto floor slab (footprint + 8 — a large
    // Minecraft import at crop×scale can be thousands of units wide) and a wide
    // same-colour terrain slab from the import greedy-merge. Either one made the
    // whole map fail to save with a misleading "each box needs p[3] and s[3]"
    // error. Tile any oversized box into a grid of ≤SPLIT_MAX pieces: adjacent
    // tiles share a face so collision/visuals are identical, and normal (in-cap)
    // boxes pass through untouched. Floor tiles keep p[1] < 0 so loadData still
    // skips them on re-open.
    const SPLIT_MAX = 1900;
    type ExportBox = { p: number[]; s: number[]; color: string; tag?: string };
    const splitBox = (box: ExportBox): ExportBox[] => {
      const [sx, sy, sz] = box.s;
      if (sx <= SPLIT_MAX && sy <= SPLIT_MAX && sz <= SPLIT_MAX) return [box];
      const nx = Math.ceil(sx / SPLIT_MAX), ny = Math.ceil(sy / SPLIT_MAX), nz = Math.ceil(sz / SPLIT_MAX);
      const tx = sx / nx, ty = sy / ny, tz = sz / nz;
      const x0 = box.p[0] - sx / 2, y0 = box.p[1] - sy / 2, z0 = box.p[2] - sz / 2;
      const out: ExportBox[] = [];
      for (let i = 0; i < nx; i++) for (let j = 0; j < ny; j++) for (let k = 0; k < nz; k++)
        out.push({ ...box, p: [x0 + tx * (i + 0.5), y0 + ty * (j + 0.5), z0 + tz * (k + 0.5)], s: [tx, ty, tz] });
      return out;
    };
    const rawBoxes: ExportBox[] = [floor, ...blocks.map((b) => ({ p: [b.x, b.y, b.z], s: [b.sx, b.sy, b.sz], color: b.color, ...(b.tag ? { tag: b.tag } : {}) }))];
    const boxes = rawBoxes.flatMap(splitBox);
    const sp = autoSpawns();
    const gameplay = autoGameplay();
    return {
      name: name.trim() || "My Map", theme,
      modes: ["gungame", "descent", "ffa", "onegun", "burnout", "arena", "treasure", "purge", "horde", "relay", "plunder"],
      boxes,
      spawns: sp.map((s) => ({ p: [s.x, s.y, s.z], yaw: s.yaw })),
      ...gameplay,
    };
  };
  const loadData = (data: any): void => {
    for (const b of blocks) scene.remove(b.mesh); blocks.length = 0;
    for (const s of spawns) scene.remove(s.mesh); spawns.length = 0;
    for (const b of (data.boxes ?? []) as any[]) {
      if (b.p[1] < 0) continue; // skip the floor slab (auto-added on export)
      addBlock(b.p[0], b.p[1], b.p[2], Math.max(0.25, b.s[0]), Math.max(0.25, b.s[1]), Math.max(0.25, b.s[2]), b.color ?? "#c07680", typeof b.tag === "string" ? b.tag : undefined);
    }
    for (const s of (data.spawns ?? []) as any[]) addSpawn(s.p[0], s.p[2], s.yaw ?? 0);
    theme = data.theme === "neon" ? "neon" : "day";
    resetHistory(); // a fresh map load is a clean slate — no undo across it
    refreshCount();
  };

  const clearMap = (): void => {
    for (const b of blocks) scene.remove(b.mesh);
    blocks.length = 0;
    for (const s of spawns) scene.remove(s.mesh);
    spawns.length = 0;
  };

  // ---- undo / redo history --------------------------------------------------
  // Every destructive edit (place / remove / paint / spawn / crop / import /
  // New) snapshots the LIGHTWEIGHT block+spawn data (plain numbers+strings, no
  // three.js meshes) BEFORE it runs; undo rebuilds the prior state from that
  // snapshot, re-drawing meshes off the shared geometry/material caches. Two
  // bounds keep memory sane on huge Minecraft imports: a depth cap, and a
  // total-stored-block budget that evicts the oldest snapshots — so hand-editing
  // keeps deep history while a 30k-box import still leaves at least one level to
  // undo. Loading a saved map (or Delete) RESETS the stacks: you cannot undo
  // across a map load.
  interface BlockSnap { x: number; y: number; z: number; sx: number; sy: number; sz: number; color: string; tag?: string; }
  interface EditSnap { blocks: BlockSnap[]; spawns: { x: number; z: number; yaw: number }[]; }
  const undoStack: EditSnap[] = [];
  const redoStack: EditSnap[] = [];
  const HISTORY_DEPTH = 60;
  const HISTORY_BLOCK_BUDGET = 240000;
  const undoBtn = el("button", { class: "seg", text: "↩ Undo" }) as HTMLButtonElement;
  const redoBtn = el("button", { class: "seg", text: "↪ Redo" }) as HTMLButtonElement;
  const updateHistoryButtons = (): void => {
    undoBtn.disabled = undoStack.length === 0;
    redoBtn.disabled = redoStack.length === 0;
  };
  const snapshot = (): EditSnap => ({
    blocks: blocks.map((b) => ({ x: b.x, y: b.y, z: b.z, sx: b.sx, sy: b.sy, sz: b.sz, color: b.color, tag: b.tag })),
    spawns: spawns.map((s) => ({ x: s.x, z: s.z, yaw: s.yaw })),
  });
  const storedBlocks = (): number => {
    let n = 0;
    for (const s of undoStack) n += s.blocks.length;
    for (const s of redoStack) n += s.blocks.length;
    return n;
  };
  /** Record the CURRENT state so the edit about to run can be undone. */
  const pushHistory = (): void => {
    undoStack.push(snapshot());
    redoStack.length = 0; // a fresh edit forks history — the redo branch is gone
    while (undoStack.length > HISTORY_DEPTH) undoStack.shift();
    while (undoStack.length > 1 && storedBlocks() > HISTORY_BLOCK_BUDGET) undoStack.shift();
    updateHistoryButtons();
  };
  const resetHistory = (): void => { undoStack.length = 0; redoStack.length = 0; updateHistoryButtons(); };
  const restoreSnap = (snap: EditSnap): void => {
    clearMap();
    for (const b of snap.blocks) addBlock(b.x, b.y, b.z, b.sx, b.sy, b.sz, b.color, b.tag);
    for (const s of snap.spawns) addSpawn(s.x, s.z, s.yaw);
    refreshCount();
  };
  const undo = (): void => {
    if (!undoStack.length) return;
    redoStack.push(snapshot());
    restoreSnap(undoStack.pop()!);
    updateHistoryButtons();
    setStatus(`Undo — ${blocks.length} blocks · ${spawns.length} spawns`);
  };
  const redo = (): void => {
    if (!redoStack.length) return;
    undoStack.push(snapshot());
    restoreSnap(redoStack.pop()!);
    updateHistoryButtons();
    setStatus(`Redo — ${blocks.length} blocks · ${spawns.length} spawns`);
  };
  undoBtn.onclick = undo;
  redoBtn.onclick = redo;
  updateHistoryButtons();

  // ---- crop mode enter/apply/cancel (UI buttons live in the side panel) ----
  const setCropUI = (): void => {
    cropBtn.style.display = cropMode ? "none" : "";
    cropRow.style.display = cropMode ? "" : "none";
  };
  const enterCrop = (): void => {
    if (!blocks.length) { setStatus("Nothing to crop yet — import or place blocks first.", true); return; }
    const fp = mapFootprint();
    cropSaved = { az, pol, rad, tx: target.x, ty: target.y, tz: target.z };
    cropMode = true;
    cropDrag = null; cropWorld = null; updateCropRectDiv();
    target.set((fp.minX + fp.maxX) / 2, 0, (fp.minZ + fp.maxZ) / 2);
    az = 0; pol = 0.05; rad = Math.min(1800, Math.max(40, fp.span * 1.1 + 24));
    applyCam();
    setCropUI();
    setStatus("Top view — drag a rectangle around the part to KEEP, then Apply crop. Wheel zooms.");
  };
  const exitCrop = (): void => {
    cropMode = false;
    cropDrag = null; cropWorld = null; updateCropRectDiv();
    if (cropSaved) { az = cropSaved.az; pol = cropSaved.pol; rad = cropSaved.rad; target.set(cropSaved.tx, cropSaved.ty, cropSaved.tz); cropSaved = null; }
    applyCam();
    setCropUI();
    refreshCount();
  };
  const applyCrop = (): void => {
    if (!cropWorld) { setStatus("Drag a rectangle around the area to keep first.", true); return; }
    const { minX, maxX, minZ, maxZ } = cropWorld;
    // Boxes straddling the edge are CLIPPED to the rectangle, not deleted —
    // merged import slabs can span the whole map, and deleting them would carve
    // huge chunks out of the kept area.
    const keptBlocks: { x: number; y: number; z: number; sx: number; sy: number; sz: number; color: string; tag?: string }[] = [];
    for (const b of blocks) {
      const x0 = Math.max(b.x - b.sx / 2, minX), x1 = Math.min(b.x + b.sx / 2, maxX);
      const z0 = Math.max(b.z - b.sz / 2, minZ), z1 = Math.min(b.z + b.sz / 2, maxZ);
      if (x1 - x0 < 0.05 || z1 - z0 < 0.05) continue;
      keptBlocks.push({ x: (x0 + x1) / 2, y: b.y, z: (z0 + z1) / 2, sx: x1 - x0, sy: b.sy, sz: z1 - z0, color: b.color, tag: b.tag });
    }
    if (!keptBlocks.length) { setStatus("That rectangle removes every block — drag a larger one.", true); return; }
    const keptSpawns = spawns
      .filter((s) => s.x >= minX && s.x <= maxX && s.z >= minZ && s.z <= maxZ)
      .map((s) => ({ x: s.x, z: s.z, yaw: s.yaw }));
    const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2; // recenter the kept area on the origin
    pushHistory();
    clearMap();
    for (const b of keptBlocks) addBlock(b.x - cx, b.y, b.z - cz, b.sx, b.sy, b.sz, b.color, b.tag);
    for (const s of keptSpawns) addSpawn(s.x - cx, s.z - cz, s.yaw);
    if (!spawns.length) addSpawn(0, 0, 0);
    cropMode = false; cropSaved = null;
    cropDrag = null; cropWorld = null; updateCropRectDiv(); setCropUI();
    const fp = mapFootprint();
    target.set(0, 4, 0); pol = Math.PI * 0.32; rad = Math.min(1800, Math.max(40, fp.span * 0.9 + 24)); applyCam();
    notify(`Cropped to ${Math.round(maxX - minX)}×${Math.round(maxZ - minZ)} — ${blocks.length} boxes kept, recentered on origin.`);
    refreshCount();
  };

  // ---- Minecraft import → 1:1 voxel grid, greedy-merged into editable boxes ----
  /** Merge the grid (see mcImport.ts) and add the resulting boxes as blocks.
   *  If the merge exceeds the editor's box budget, the grid AUTO-COARSENS
   *  (halved per pass) until it fits — dropping the 17k smallest boxes of a
   *  spruce forest left it full of holes; a complete 2x-coarse forest reads
   *  far better. */
  const buildGridBlocks = (
    grid: Uint8Array, nx: number, ny: number, nz: number, palette: string[],
    o: { unit: number; ox: number; oy: number; oz: number; cullInterior: boolean; glassColors?: Set<string> },
  ): { merged: number; placed: number; coarsened: number } => {
    let g = grid, gx = nx, gy = ny, gz = nz, unit = o.unit, coarsened = 1;
    let merged = mergeGrid(g, gx, gy, gz, o.cullInterior);
    while (merged.length > MAX_BLOCKS && Math.max(gx, gy, gz) > 4 && coarsened < 8) {
      ({ grid: g, nx: gx, ny: gy, nz: gz } = downsampleGrid(g, gx, gy, gz));
      unit *= 2;
      coarsened *= 2;
      merged = mergeGrid(g, gx, gy, gz, o.cullInterior);
    }
    for (const c of merged.slice(0, MAX_BLOCKS)) {
      const w = c.bx1 - c.bx0 + 1, h = c.by1 - c.by0 + 1, d = c.bz1 - c.bz0 + 1;
      const col = palette[c.color - 1] ?? "#888888";
      addBlock(
        o.ox + (c.bx0 + w / 2) * unit, o.oy + (c.by0 + h / 2) * unit, o.oz + (c.bz0 + d / 2) * unit,
        w * unit, h * unit, d * unit, col, o.glassColors?.has(col) ? "mcglass" : "minecraft");
    }
    return { merged: merged.length, placed: Math.min(merged.length, MAX_BLOCKS), coarsened };
  };

  const importSchematic = async (file: File): Promise<void> => {
    setStatus("Importing…");
    try {
      let buf = await file.arrayBuffer();
      try { buf = await decompress(buf, "gzip"); } catch { /* maybe already uncompressed NBT */ }
      const root = parseNBT(buf);
      const dec = decodeAnySchematic(root);
      if (!dec) { setStatus("Unsupported file — need a classic .schematic, a WorldEdit .schem, or a structure-block .nbt.", true); return; }
      const { W, H, L } = dec;
      // Every block is read — no sampling. Step only rises if the volume would
      // not fit the grid budget (schematics practically never hit it).
      const userStep = Math.max(1, Math.round(importStep));
      let step = userStep;
      let nx = Math.ceil(W / step), ny = Math.ceil(H / step), nz = Math.ceil(L / step);
      while (nx * ny * nz > MAX_GRID_CELLS && step < 32) {
        step++;
        nx = Math.ceil(W / step); ny = Math.ceil(H / step); nz = Math.ceil(L / step);
      }
      const grid = new Uint8Array(nx * ny * nz);
      const { palette, colorIdx } = makePalette();
      dec.each((x, y, z, color) => {
        if (x < 0 || y < 0 || z < 0 || x >= W || y >= H || z >= L) return; // corrupt entries
        const gi = (Math.floor(y / step) * nz + Math.floor(z / step)) * nx + Math.floor(x / step);
        if (!grid[gi]) grid[gi] = colorIdx(color);
      });
      pushHistory();
      clearMap();
      const unit = step * importScale;
      const res = buildGridBlocks(grid, nx, ny, nz, palette, {
        unit, ox: -(nx * unit) / 2, oy: 0, oz: -(nz * unit) / 2, cullInterior: true,
      });
      const g = spawnMarker(); g.position.set(0, 0, 0); scene.add(g); spawns.push({ x: 0, z: 0, yaw: 0, mesh: g });
      rad = Math.max(W, H, L) * importScale + 30; target.set(0, (H * importScale) / 2, 0); applyCam(); refreshCount();
      const dropped = res.merged - res.placed;
      notify(`Import finished: ${res.placed}/${res.merged} boxes loaded.`);
      setStatus(`Imported ${res.placed}/${res.merged} boxes${step > 1 ? ` · step ${step}` : " · 1:1"}.${dropped > 0 ? ` ${dropped} smallest boxes dropped (over the ${MAX_BLOCKS} limit) — use ✂️ Crop to trim.` : ""}`);
    } catch (e) { setStatus("Import failed: " + (e as Error).message, true); }
  };

  /** Cached region scan for the current world-folder import — lets the Scene
   *  ‹ › buttons jump between detected builds without re-reading every file. */
  let mcaCtx: { sorted: { f: File; rc: { rx: number; rz: number } }[]; scenes: Scene[] } | null = null;
  let mcaSceneIdx = 0;

  const importMcaFiles = async (filesIn: File[]): Promise<void> => {
    const files = filesIn.filter(isOverworldRegionFile);
    if (!files.length) { setStatus("No region/*.mca files found.", true); return; }
    if (!("DecompressionStream" in globalThis)) { setStatus("This browser cannot decompress Minecraft regions.", true); return; }
    const sorted = files.map((f) => ({ f, rc: regionCoords(f) })).filter((x): x is { f: File; rc: { rx: number; rz: number } } => !!x.rc)
      .sort((a, b) => Math.hypot(a.rc.rx, a.rc.rz) - Math.hypot(b.rc.rx, b.rc.rz));
    const scanChunks: ScanChunk[] = [];
    const levelDat = filesIn.find((f) => /(?:^|\/)level\.dat$/i.test((f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name));
    if (levelDat) {
      try {
        const levelRoot = parseNBT(await decompress(await levelDat.arrayBuffer(), "gzip"));
        const data = (levelRoot.Data ?? {}) as Record<string, unknown>;
        if (Number.isFinite(Number(data.SpawnX)) && Number.isFinite(Number(data.SpawnZ))) {
          importCenterX = Number(data.SpawnX);
          importCenterZ = Number(data.SpawnZ);
          if (importCenterXInput) importCenterXInput.value = String(importCenterX);
          if (importCenterZInput) importCenterZInput.value = String(importCenterZ);
        }
        if (typeof data.LevelName === "string") { name = data.LevelName; nameInput.value = name; }
      } catch {
        // Some exports omit level.dat or strip it; manual centre controls cover that.
      }
    }
    setStatus(`Importing ${files.length} region files…`);
    for (let fi = 0; fi < sorted.length; fi++) {
      const { f, rc } = sorted[fi];
      await tick();
      setProgress(0.05 + 0.7 * (fi / sorted.length), `Reading region ${fi + 1}/${sorted.length}…`);
      const buf = await f.arrayBuffer();
      if (buf.byteLength < 8192) continue;
      const dv = new DataView(buf);
      for (let i = 0; i < 1024; i++) {
        const cx = i & 31, cz = i >> 5;
        const chunkX = rc.rx * 32 + cx, chunkZ = rc.rz * 32 + cz;
        const off = (dv.getUint8(i * 4) << 16) | (dv.getUint8(i * 4 + 1) << 8) | dv.getUint8(i * 4 + 2);
        const sectors = dv.getUint8(i * 4 + 3);
        if (!off || !sectors) continue;
        const pos = off * 4096;
        // The 24-bit `off` comes straight from the region header — a corrupt or
        // malicious .mca can point it past EOF. getUint32(pos)/getUint8(pos+4)
        // THROW on an out-of-range read, and the import runs as `void
        // importMcaFiles(...)` with no .catch, so one bad chunk would silently
        // kill the whole import. Bounds-check BEFORE reading.
        if (pos + 5 > buf.byteLength) continue;
        const len = dv.getUint32(pos);
        const comp = dv.getUint8(pos + 4);
        if (!len || pos + 4 + len > buf.byteLength) continue;
        const payload = buf.slice(pos + 5, pos + 4 + len);
        let chunkBuf: ArrayBuffer;
        try {
          chunkBuf = comp === 1 ? await decompress(payload, "gzip") : comp === 2 ? await decompress(payload, "deflate") : payload;
        } catch {
          continue;
        }
        let root: Record<string, unknown>;
        try { root = parseNBT(chunkBuf); } catch { continue; }
        const level = (root.Level && typeof root.Level === "object" ? root.Level : root) as Record<string, unknown>;
        const sections = (level.sections ?? level.Sections) as unknown[];
        if (!Array.isArray(sections)) continue;
        const chunkSections = sections as Record<string, unknown>[];
        let structureCount = 0;
        let structOnly = 0;
        let chunkYMin = Infinity, chunkYMax = -Infinity;
        const scanStride = 4;
        for (const sec of chunkSections) {
          const secY = Number(sec.Y ?? sec.y ?? 0);
          const blockStates = (sec.block_states ?? {}) as Record<string, unknown>;
          const palette = (blockStates.palette ?? sec.Palette) as unknown[];
          // VEGETATION counts toward the score now: a landscaped build (what a
          // map's screenshot shows) must outrank a same-size windowless
          // machinery vault — the escape-house test world centred the import on
          // its hidden command-block room before this.
          if (!Array.isArray(palette) || !palette.length || !palette.some((p) => {
            const name = paletteName(p);
            return mcSolid(name) && (mcStructure(name) || mcVegetation(name));
          })) continue;
          const data = (blockStates.data ?? sec.BlockStates) as bigint[] | undefined;
          const bits = Math.max(4, Math.ceil(Math.log2(Math.max(1, palette.length))));
          for (let y = 0; y < 16; y += scanStride) {
            const wy = secY * 16 + y;
            for (let z = 0; z < 16; z += scanStride) for (let x = 0; x < 16; x += scanStride) {
              const pi = palette.length === 1 ? 0 : blockStateIndex(data, (y * 16 + z) * 16 + x, bits);
              const name = paletteName(palette[Math.min(palette.length - 1, pi)]);
              if (mcSolid(name) && (mcStructure(name) || mcVegetation(name))) {
                // STRUCTURE weighs 3x vegetation: greenery only breaks the tie
                // between a build and a machinery vault — on a natural-terrain
                // world equal weighting made every forest outrank the castle.
                const isStruct = mcStructure(name) && !mcNaturalWood(name);
                structureCount += scanStride * scanStride * scanStride * (isStruct ? 3 : 1);
                if (isStruct) structOnly += scanStride * scanStride * scanStride;
                chunkYMin = Math.min(chunkYMin, wy);
                chunkYMax = Math.max(chunkYMax, wy + scanStride);
              }
            }
          }
        }
        if (structureCount > 0) scanChunks.push({ chunkX, chunkZ, n: structureCount, structN: structOnly, yMin: chunkYMin, yMax: chunkYMax });
      }
    }
    // Group the scan into distinct build SCENES (adventure maps scatter several
    // builds hundreds of blocks apart) — the ‹ › Scene buttons cycle them
    // without re-reading the region files.
    mcaCtx = { sorted, scenes: detectScenes(scanChunks) };
    mcaSceneIdx = 0;
    updateSceneRow();
    await buildMcaScene();
  };

  /** Import ONE detected scene from the cached region scan. Crop, centre and
   *  Y-range all refit to the chosen scene (the old global Y-range stretched
   *  every import down to the deepest machinery room in the world). */
  const buildMcaScene = async (): Promise<void> => {
    if (!mcaCtx) return;
    const { sorted, scenes } = mcaCtx;
    const pick = scenes.length ? scenes[Math.min(mcaSceneIdx, scenes.length - 1)] : null;
    if (pick) {
      importCenterX = pick.x;
      importCenterZ = pick.z;
      if (importCenterXInput) importCenterXInput.value = String(importCenterX);
      if (importCenterZInput) importCenterZInput.value = String(importCenterZ);
      importCrop = Math.max(200, Math.min(2048, Math.ceil((pick.span + 64) / 16) * 16));
      if (importCropInput) importCropInput.value = String(importCrop);
      importYMin = Math.max(-64, Math.floor(pick.yMin - 4));
      importYMax = Math.min(384, Math.max(importYMin + 1, Math.ceil(pick.yMax + 8)));
      if (importYMinInput) importYMinInput.value = String(importYMin);
      if (importYMaxInput) importYMaxInput.value = String(importYMax);
    }
    const half = importCrop / 2;
    pushHistory();
    clearMap();
    // Dense voxel grid over the crop. The old Map-of-string-keys version had a
    // 90k raw-cell budget and BROKE OUT of the region loop when it filled — any
    // real crop has millions of solid cells, so whole areas silently went
    // missing. A flat Uint8Array holds every cell; if the crop is too big for
    // the budget the STEP rises instead, so the whole area still imports
    // (coarser beats holes).
    const userStep = Math.max(1, Math.round(importStep));
    let step = userStep;
    let nx = Math.ceil(importCrop / step);
    let ny = Math.max(1, Math.ceil((importYMax - importYMin) / step));
    let nz = nx;
    while (nx * ny * nz > MAX_GRID_CELLS && step < 32) {
      step++;
      nx = Math.ceil(importCrop / step);
      ny = Math.max(1, Math.ceil((importYMax - importYMin) / step));
      nz = nx;
    }
    const grid = new Uint8Array(nx * ny * nz);
    // At step > 1 several world blocks share a cell — the HIGHEST one must win
    // the colour (first-write-wins let the block UNDER a surface claim it, so
    // coarse imports of grassland came out dirt-brown). gridTop tracks the
    // sub-height of the block that painted each cell.
    const gridTop = step > 1 ? new Uint8Array(nx * ny * nz) : null;
    const { palette: importPalette, colorIdx } = makePalette();
    // SHAPED blocks (slabs/stairs/fences/panes) become exact sub-boxes instead
    // of full cubes — but only at step 1 (at a coarser step the cell is bigger
    // than the shape anyway) and within a budget that keeps the total under
    // the editor's box cap.
    const extras: { x: number; y: number; z: number; sx: number; sy: number; sz: number; color: string; glass: boolean }[] = [];
    const glassColors = new Set<string>(); // colours that must carry the mcglass tag
    const EXTRA_CAP = 9000;
    const emitCell = (wx: number, y: number, wz: number, act: BlockAction): void => {
      const lx = wx - importCenterX;
      const lz = wz - importCenterZ;
      if (lx < -half || lx >= half || lz < -half || lz >= half || y < importYMin || y >= importYMax) return;
      if (importDetail === "structure" && !act.struct) return;
      if (act.kind === "extra" && step === 1 && act.boxes && extras.length + act.boxes.length <= EXTRA_CAP) {
        for (const b of act.boxes) {
          extras.push({
            x: (lx + b.dx + b.sx / 2) * importScale,
            y: (y - importYMin + b.dy + b.sy / 2) * importScale,
            z: (lz + b.dz + b.sz / 2) * importScale,
            sx: b.sx * importScale, sy: b.sy * importScale, sz: b.sz * importScale,
            color: act.color, glass: !!act.glass,
          });
        }
        return;
      }
      const bx = Math.floor((lx + half) / step);
      const by = Math.floor((y - importYMin) / step);
      const bz = Math.floor((lz + half) / step);
      const gi = (by * nz + bz) * nx + bx;
      if (act.glass) glassColors.add(act.color);
      if (!gridTop) {
        if (!grid[gi]) grid[gi] = colorIdx(act.color);
        return;
      }
      const sub = (y - importYMin) - by * step;
      if (!grid[gi] || sub >= gridTop[gi]) {
        grid[gi] = colorIdx(act.color);
        gridTop[gi] = sub;
      }
    };
    await tick();
    setProgress(0.82, "Building geometry…");
    for (let fi = 0; fi < sorted.length; fi++) {
      const { f, rc } = sorted[fi];
      const regionMinX = rc.rx * 512, regionMinZ = rc.rz * 512;
      if (regionMinX >= importCenterX + half || regionMinX + 511 < importCenterX - half ||
          regionMinZ >= importCenterZ + half || regionMinZ + 511 < importCenterZ - half) continue;
      await tick();
      setProgress(0.82 + 0.12 * (fi / sorted.length), `Building geometry ${fi + 1}/${sorted.length}…`);
      const buf = await f.arrayBuffer();
      if (buf.byteLength < 8192) continue;
      const dv = new DataView(buf);
      for (let i = 0; i < 1024; i++) {
        const cx = i & 31, cz = i >> 5;
        const chunkX = rc.rx * 32 + cx, chunkZ = rc.rz * 32 + cz;
        if (chunkX * 16 >= importCenterX + half || chunkX * 16 + 15 < importCenterX - half ||
            chunkZ * 16 >= importCenterZ + half || chunkZ * 16 + 15 < importCenterZ - half) continue;
        const off = (dv.getUint8(i * 4) << 16) | (dv.getUint8(i * 4 + 1) << 8) | dv.getUint8(i * 4 + 2);
        const sectors = dv.getUint8(i * 4 + 3);
        if (!off || !sectors) continue;
        const pos = off * 4096;
        // The 24-bit `off` comes straight from the region header — a corrupt or
        // malicious .mca can point it past EOF. getUint32(pos)/getUint8(pos+4)
        // THROW on an out-of-range read, and the import runs as `void
        // importMcaFiles(...)` with no .catch, so one bad chunk would silently
        // kill the whole import. Bounds-check BEFORE reading.
        if (pos + 5 > buf.byteLength) continue;
        const len = dv.getUint32(pos);
        const comp = dv.getUint8(pos + 4);
        if (!len || pos + 4 + len > buf.byteLength) continue;
        const payload = buf.slice(pos + 5, pos + 4 + len);
        let chunkBuf: ArrayBuffer;
        try {
          chunkBuf = comp === 1 ? await decompress(payload, "gzip") : comp === 2 ? await decompress(payload, "deflate") : payload;
        } catch {
          continue;
        }
        let root: Record<string, unknown>;
        try { root = parseNBT(chunkBuf); } catch { continue; }
        const level = (root.Level && typeof root.Level === "object" ? root.Level : root) as Record<string, unknown>;
        const sections = (level.sections ?? level.Sections) as unknown[];
        if (!Array.isArray(sections)) continue;
        for (const sec of sections as Record<string, unknown>[]) {
          const secY = Number(sec.Y ?? sec.y ?? 0);
          const blockStates = (sec.block_states ?? {}) as Record<string, unknown>;
          const palette = (blockStates.palette ?? sec.Palette) as unknown[];
          if (!Array.isArray(palette) || !palette.length) continue;
          const data = (blockStates.data ?? sec.BlockStates) as bigint[] | undefined;
          const bits = Math.max(4, Math.ceil(Math.log2(Math.max(1, palette.length))));
          // classify each palette entry ONCE — the old per-block regex chain
          // (mcSolid + mcNameColor on all 4096 blocks of every section) was the
          // import's hottest loop
          const actions = palette.map((p) => classifyBlock(p));
          for (let y = 0; y < 16; y++) {
            const wy = secY * 16 + y;
            if (wy < importYMin || wy >= importYMax) continue;
            for (let z = 0; z < 16; z++) for (let x = 0; x < 16; x++) {
              const pi = palette.length === 1 ? 0 : blockStateIndex(data, (y * 16 + z) * 16 + x, bits);
              const act = actions[Math.min(actions.length - 1, pi)];
              if (act.kind !== "skip") emitCell(chunkX * 16 + x, wy, chunkZ * 16 + z, act);
            }
          }
        }
      }
    }
    await tick();
    setProgress(0.96, "Merging boxes…");
    const unit = step * importScale;
    const res = buildGridBlocks(grid, nx, ny, nz, importPalette, {
      unit, ox: -half * importScale, oy: 0, oz: -half * importScale, cullInterior: importDetail !== "solid", glassColors,
    });
    // shaped sub-boxes (slabs, stairs, fences, panes) land on top of the merged
    // grid — they bypassed it so their cells read as open space
    for (const e of extras) addBlock(e.x, e.y, e.z, e.sx, e.sy, e.sz, e.color, e.glass ? "mcglass" : "minecraft");
    const g = spawnMarker(); g.position.set(0, 0, 0); scene.add(g); spawns.push({ x: 0, z: 0, yaw: 0, mesh: g });
    rad = importCrop * importScale * 0.9 + 30; target.set(0, ((importYMax - importYMin) * importScale) / 2, 0); applyCam(); refreshCount();
    const dropped = res.merged - res.placed;
    finishProgress(`Import finished: ${res.placed}/${res.merged} boxes + ${extras.length} shaped details.`);
    setStatus(`Imported ${res.placed}/${res.merged} boxes around X ${importCenterX}, Z ${importCenterZ}, crop ${importCrop}${step > 1 ? ` · step ${step}` : " · 1:1"}.` +
      `${scenes.length > 1 ? ` Scene ${mcaSceneIdx + 1}/${scenes.length} — cycle detected builds with the Scene ‹ › buttons.` : ""}` +
      `${step > userStep ? ` Step auto-raised to ${step} to fit this crop — shrink Crop and re-import for full 1:1.` : ""}` +
      `${res.coarsened > 1 ? ` Auto-coarsened ${res.coarsened}x to fit the ${MAX_BLOCKS}-box limit — shrink Crop for finer detail.` : ""}` +
      `${dropped > 0 ? ` ${dropped} smallest boxes over the ${MAX_BLOCKS} limit were dropped — trim with ✂️ Crop and re-import a smaller area.` : " Trim with ✂️ Crop, then Save."}`);
  };

  const fileInput = el("input", { type: "file", accept: ".schematic,.schem,.nbt,.mca", multiple: "1", style: "display:none" }) as HTMLInputElement;
  fileInput.onchange = () => {
    const files = [...(fileInput.files ?? [])];
    if (files.some((f) => /\.mca$/i.test(f.name))) void importMcaFiles(files);
    else if (files[0]) void importSchematic(files[0]);
    fileInput.value = "";
  };
  const folderInput = el("input", { type: "file", multiple: "1", style: "display:none" }) as HTMLInputElement;
  folderInput.setAttribute("webkitdirectory", "");
  folderInput.onchange = () => { void importMcaFiles([...(folderInput.files ?? [])]); folderInput.value = ""; };
  const importBtn = el("button", { class: "ghost wide mt1", text: "📥 Import Minecraft file" });
  importBtn.onclick = () => fileInput.click();
  const importFolderBtn = el("button", { class: "ghost wide", text: "📁 Import Minecraft world folder" });
  importFolderBtn.onclick = () => folderInput.click();
  // SCENE PICKER: adventure worlds hold several distinct builds — cycle them
  const scenePrevBtn = el("button", { class: "ghost round", text: "‹" });
  const sceneNextBtn = el("button", { class: "ghost round", text: "›" });
  const sceneLabel = el("span", { class: "hint", style: "flex:1;text-align:center" });
  const sceneRow = el("div", { style: "display:none;align-items:center;gap:0.4rem;margin-top:0.3rem" }, scenePrevBtn, sceneLabel, sceneNextBtn);
  const updateSceneRow = (): void => {
    const n = mcaCtx?.scenes.length ?? 0;
    sceneRow.style.display = n > 1 ? "flex" : "none";
    if (n > 0) sceneLabel.textContent = `Scene ${mcaSceneIdx + 1}/${n} · biggest build first`;
  };
  const cycleScene = (dir: number): void => {
    if (!mcaCtx || mcaCtx.scenes.length < 2) return;
    mcaSceneIdx = (mcaSceneIdx + dir + mcaCtx.scenes.length) % mcaCtx.scenes.length;
    updateSceneRow();
    void buildMcaScene();
  };
  scenePrevBtn.onclick = () => cycleScene(-1);
  sceneNextBtn.onclick = () => cycleScene(1);

  // ---- side panel ----
  const nameInput = el("input", { type: "text", value: name, maxlength: "40" }) as HTMLInputElement;
  nameInput.oninput = () => { name = nameInput.value; };
  const colorInput = el("input", { type: "color", value: color }) as HTMLInputElement;
  colorInput.oninput = () => { color = colorInput.value; };
  const numInput = (value: number, min: number, max: number, step: number, onInput: (v: number) => void): HTMLInputElement => {
    const input = el("input", { type: "number", value: String(value), min: String(min), max: String(max), step: String(step) }) as HTMLInputElement;
    input.oninput = () => onInput(Math.max(min, Math.min(max, Number(input.value) || value)));
    return input;
  };
  const brushXInput = numInput(brushX, 0.5, 80, 0.5, (v) => { brushX = v; });
  const brushYInput = numInput(brushY, 0.5, 80, 0.5, (v) => { brushY = v; });
  const brushZInput = numInput(brushZ, 0.5, 80, 0.5, (v) => { brushZ = v; });
  const presetRow = el("div", { class: "seg-row" });
  const setBrush = (x: number, y: number, z: number): void => {
    brushX = x; brushY = y; brushZ = z;
    brushXInput.value = String(x); brushYInput.value = String(y); brushZInput.value = String(z);
  };
  for (const [label, dims] of [
    ["Cube", [4, 4, 4]],
    ["Floor", [16, 1, 16]],
    ["Wall", [16, 8, 1]],
    ["Pillar", [2, 12, 2]],
    ["Stair", [8, 2, 8]],
  ] as const) {
    const b = el("button", { class: "seg", text: label });
    b.onclick = () => setBrush(dims[0], dims[1], dims[2]);
    presetRow.append(b);
  }
  const themeBtn = el("button", { class: "seg", text: `Theme: ${theme}` });
  themeBtn.onclick = () => { theme = theme === "day" ? "neon" : "day"; themeBtn.textContent = `Theme: ${theme}`; };

  const toolRow = el("div", { class: "seg-row" });
  const toolBtn = (label: string, t: typeof tool): HTMLElement => {
    const b = el("button", { class: "seg" + (t === tool ? " active" : ""), text: label });
    b.onclick = () => { tool = t; toolRow.querySelectorAll("button").forEach((x) => x.classList.remove("active")); b.classList.add("active"); };
    return b;
  };
  toolRow.append(toolBtn("🧱 Place", "place"), toolBtn("🎨 Paint", "paint"), toolBtn("⛏ Remove", "remove"), toolBtn("◉ Spawn", "spawn"));

  const cropBtn = el("button", { class: "ghost wide", text: "✂️ Crop map (top view)" });
  cropBtn.onclick = enterCrop;
  const cropApplyBtn = el("button", { class: "primary wide", text: "✔ Apply crop" });
  cropApplyBtn.onclick = applyCrop;
  const cropCancelBtn = el("button", { class: "ghost wide", text: "✖ Cancel crop" });
  cropCancelBtn.onclick = exitCrop;
  const cropRow = el("div", { style: "display:none" }, cropApplyBtn, cropCancelBtn);

  const importCropInput = numInput(importCrop, 16, 2048, 8, (v) => { importCrop = v; });
  importCenterXInput = numInput(importCenterX, -30000000, 30000000, 1, (v) => { importCenterX = Math.round(v); });
  importCenterZInput = numInput(importCenterZ, -30000000, 30000000, 1, (v) => { importCenterZ = Math.round(v); });
  const importYMinInput = numInput(importYMin, -64, 320, 1, (v) => { importYMin = Math.min(v, importYMax - 1); });
  const importYMaxInput = numInput(importYMax, -63, 384, 1, (v) => { importYMax = Math.max(v, importYMin + 1); });
  const importStepInput = numInput(importStep, 1, 32, 1, (v) => { importStep = Math.max(1, Math.round(v)); });
  const importScaleInput = numInput(importScale, 0.25, 4, 0.25, (v) => { importScale = v; });
  const importDetailSel = el("select", {}) as HTMLSelectElement;
  for (const [value, label] of [["structure", "Structure only"], ["surface", "Surface + terrain"], ["solid", "Solid + caves"]] as const) {
    const o = el("option", { value, text: label });
    if (value === importDetail) o.setAttribute("selected", "s");
    importDetailSel.append(o);
  }
  importDetailSel.onchange = () => { importDetail = importDetailSel.value === "structure" ? "structure" : importDetailSel.value === "solid" ? "solid" : "surface"; };

  const saveBtn = el("button", { class: "primary wide", text: "💾 Save" }) as HTMLButtonElement;
  const publishBtn = el("button", { class: "wide mt1", text: "🌍 Publish" }) as HTMLButtonElement;
  const setUploadBusy = (busy: boolean): void => {
    saveBtn.disabled = busy;
    publishBtn.disabled = busy;
  };
  const uploadError = (e: unknown): void => {
    const msg = (e as Error).message || "Upload failed.";
    setProgress(null);
    setStatus(msg, true);
  };
  saveBtn.onclick = async () => {
    try {
      setUploadBusy(true);
      setProgress(0.12, "Preparing map…");
      await tick();
      const data = toMapData();
      setProgress(0.45, "Uploading map…");
      await tick();
      const m = await api.saveMap(name.trim() || "My Map", data, currentId ?? undefined);
      currentId = m.id;
      finishProgress(`Saved "${m.name}" ✓`);
    } catch (e) {
      uploadError(e);
    } finally {
      setUploadBusy(false);
    }
  };
  publishBtn.onclick = async () => {
    try {
      setUploadBusy(true);
      setProgress(0.1, "Preparing map…");
      await tick();
      const data = toMapData();
      setProgress(0.38, currentId ? "Uploading latest map…" : "Uploading map before publishing…");
      await tick();
      const m = await api.saveMap(name.trim() || "My Map", data, currentId ?? undefined);
      currentId = m.id;
      setProgress(0.72, "Publishing map…");
      await api.publishMap(currentId, true);
      finishProgress(`Published "${m.name}" 🌍 ✓`);
    } catch (e) {
      uploadError(e);
    } finally {
      setUploadBusy(false);
    }
  };
  const mineBtn = el("button", { class: "ghost wide mt1", text: "📂 My Maps" });
  const mineList = el("div", { class: "editor-mine" });
  const loadMyMaps = async (): Promise<void> => {
    mineList.replaceChildren(el("div", { class: "hint", text: "Loading…" }));
    try {
      const maps = await api.listMyMaps();
      mineList.replaceChildren(...(maps.length ? maps.map((mm) => {
        const edit = el("button", { class: "seg", text: "Edit" });
        edit.onclick = async () => { const full = await api.getMap(mm.id); currentId = full.id; name = full.name; nameInput.value = full.name; loadData(full.data); setStatus(`Editing "${full.name}"`); };
        const del = el("button", { class: "seg danger", text: "Delete" });
        del.onclick = async () => {
          if (!window.confirm(`Delete "${mm.name}"?`)) return;
          try {
            await api.deleteMap(mm.id);
            if (currentId === mm.id) {
              currentId = null;
              name = "My Map";
              nameInput.value = name;
              clearMap();
              resetHistory();
              refreshCount();
            }
            setStatus(`Deleted "${mm.name}"`);
            await loadMyMaps();
          } catch (e) { setStatus((e as Error).message, true); }
        };
        return el("div", { class: "editor-mine-row" }, el("span", { text: `${mm.name}${mm.published ? " 🌍" : ""}` }), el("div", { class: "editor-row-actions" }, edit, del));
      }) : [el("div", { class: "hint", text: "No saved maps yet." })]));
    } catch (e) { setStatus((e as Error).message, true); }
  };
  mineBtn.onclick = () => { void loadMyMaps(); };
  // Connect an AI via MCP: show the endpoint + token so an MCP-capable assistant
  // can edit the user's maps on their behalf (tools: list/get/save/publish_map).
  const mcpBtn = el("button", { class: "ghost wide mt1", text: "🤖 Connect an AI (MCP)" });
  const mcpInfo = el("div", { class: "editor-mine" });
  mcpBtn.onclick = () => {
    const url = `${location.origin}/api/mcp`;
    const tok = api.token ?? "(log in first)";
    const urlIn = el("input", { type: "text", readonly: "1", value: url }) as HTMLInputElement;
    const tokIn = el("input", { type: "text", readonly: "1", value: `Bearer ${tok}` }) as HTMLInputElement;
    urlIn.onclick = () => urlIn.select();
    tokIn.onclick = () => tokIn.select();
    mcpInfo.replaceChildren(
      el("div", { class: "hint", text: "Point an MCP-capable AI at this HTTP endpoint (JSON-RPC), with the token as a Bearer header, to let it edit your maps:" }),
      urlIn, tokIn,
      el("div", { class: "hint", text: "Tools: list_maps · get_map · save_map · publish_map. The token refreshes each time you open the game." }),
    );
  };
  const newBtn = el("button", { class: "ghost wide mt1", text: "✨ New" });
  newBtn.onclick = () => { pushHistory(); currentId = null; name = "My Map"; nameInput.value = name; clearMap(); refreshCount(); };
  const backBtn = el("button", { class: "ghost wide mt1", text: "‹ Back" });
  // Ctrl/⌘+Z = undo, Ctrl/⌘+Y or Ctrl/⌘+Shift+Z = redo. Ignored while typing in
  // a field or mid-crop-drag so it never fights text editing or the crop tool.
  const onKeyDown = (e: KeyboardEvent): void => {
    if (!running || cropMode) return;
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) return;
    const k = e.key.toLowerCase();
    if (k === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
    else if (k === "y" || (k === "z" && e.shiftKey)) { e.preventDefault(); redo(); }
  };
  window.addEventListener("keydown", onKeyDown);
  backBtn.onclick = () => { running = false; window.removeEventListener("resize", resize); window.removeEventListener("keydown", onKeyDown); renderer.dispose(); opts.onBack(); };

  const panel = el("div", { class: "editor-panel" },
    el("h2", { text: "Map Builder" }),
    el("label", { class: "sub", text: "Name" }), nameInput,
    el("div", { class: "card-title", text: "TOOL" }), toolRow,
    el("div", { class: "seg-row" }, undoBtn, redoBtn),
    el("div", { class: "sub" }, el("span", { text: "Block color" }), colorInput),
    el("div", { class: "card-title", text: "BLOCK SIZE" }),
    el("div", { class: "editor-dims" },
      el("label", {}, el("span", { text: "W" }), brushXInput),
      el("label", {}, el("span", { text: "H" }), brushYInput),
      el("label", {}, el("span", { text: "D" }), brushZInput)),
    presetRow,
    themeBtn,
    el("div", { class: "hint", text: "Left-drag = orbit · wheel = zoom · click ground/face = place · paint recolors · remove deletes blocks/spawns · Ctrl+Z undo / Ctrl+Y redo" }),
    cropBtn, cropRow,
    saveBtn, publishBtn, mineBtn, mineList,
    el("div", { class: "card-title", text: "MINECRAFT IMPORT" }),
    el("div", { class: "editor-dims" },
      el("label", {}, el("span", { text: "Crop" }), importCropInput),
      el("label", {}, el("span", { text: "Center X" }), importCenterXInput),
      el("label", {}, el("span", { text: "Center Z" }), importCenterZInput),
      el("label", {}, el("span", { text: "Y min" }), importYMinInput),
      el("label", {}, el("span", { text: "Y max" }), importYMaxInput),
      el("label", {}, el("span", { text: "Step" }), importStepInput),
      el("label", {}, el("span", { text: "Scale" }), importScaleInput)),
    el("div", { class: "sub" }, el("span", { text: "Detail" }), importDetailSel),
    importBtn, importFolderBtn, sceneRow, fileInput, folderInput, mcpBtn, mcpInfo, newBtn, backBtn, progWrap, status, notice,
  );

  screen.append(el("div", { class: "editor-wrap" }, canvasWrap, panel));

  // seed a small starter floor of blocks so there's something to click
  for (let gx = -12; gx <= 12; gx += 8) for (let gz = -12; gz <= 12; gz += 8) addBlock(snap(gx, 4), 2, snap(gz, 4), 4, 4, 4, "#3a4152");
  refreshCount();
  requestAnimationFrame(() => { resize(); applyCam(); loop(); });
  return screen;
}
